import React, { useEffect, useMemo, useState } from 'react'
import DashboardLayout from '@/components/layouts/DashboardLayout'
import useAccount from '@/hooks/api-hooks/useAccount'
import InputField from '@/components/common/fields/InputField'
import InternationalPhoneInput from '@/components/common/forms/formik/InternationalPhoneInput'
import MultiselectField from '@/components/common/fields/MultiselectField'
import { Form, Formik } from 'formik'
import useLocations from '@/hooks/api-hooks/useLocations'
import { User } from '@/api/interface/user'
import { printAxiosErrors } from '@/utils/util-functions'
import * as Yup from 'yup'
import classNames from 'classnames'
import buttonStyles from '@/styles/Buttons.module.scss'
import Colors from '@/styles/Colors.module.scss'
import { updateUser } from '@/api/nextme/users'
import { UserRoleNames } from '@/consts/UserRolesEnum'
import { Card, Dialog, DialogTitle } from '@mui/material'
import PasswordChangeForm from '@/components/common/forms/PasswordChangeForm'
import { setAlert } from '@/store/slices/alertSlice'
import { useDispatch } from 'react-redux'
import { AccountStatusEnum } from '@/consts/AccountStatusEnum'
import { Button } from '@/components/common/Button/Button'
import EmailVerifyModal from '@/components/pages/signup/EmailVerifyModal'
import ActionModalTextConfirm from '@/components/common/ActionModalTextConfirm'
import { deleteAccount } from '@/api/nextme/account'
import typeStyles from '@/styles/Typography.module.scss'
import { intlPhoneTest } from '@/utils/formik-validators'
import { useTypedSelector } from '../store'
import { cleanIntlPhoneForSubmission } from '@/utils/phone-utils'

export default function AccountPage() {
  const dispatch = useDispatch()

  const [isFormDirty, setIsFormDirty] = useState(false)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)

  const { account } = useAccount()
  const { locations } = useLocations()
  const { defaultCountryISO } = useTypedSelector(
    (state) => state.user.currentBusiness
  )

  const isUserOwner = useMemo(
    () => account?.roles.includes(UserRoleNames.bo),
    [account]
  )

  //* If a user has logged in, we can consider their invitation resolved
  useEffect(() => {
    if (account?.status === AccountStatusEnum.invited) {
      updateUser({ id: account.id, status: AccountStatusEnum.enabled })
    }
  }, [account])

  const locationOptions = useMemo(
    () =>
      locations?.map((x) => ({
        value: x.id,
        label: x.name,
      })),
    [locations]
  )

  const deleteAccountAction = () => {
    const accountId = account?.id
    deleteAccount(accountId)
  }

  const initialValues: User = {
    ...account,
    phone: account?.phone || '',
    role: account?.roles[0] || UserRoleNames.gu,
    password: '',
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    phone: Yup.string().test(
      'passes-google-libphonenumber-validation',
      'Invalid phone number',
      async (value) => await intlPhoneTest(value)
    ),
    email: Yup.string().email().required('Required'),
    locations: Yup.array()
      .test(
        'locations',
        'Must select at least 1 location',
        (val) => val.length >= 1
      )
      .min(1),
  })

  const onSubmit = async (values: User) => {
    setIsFormDirty(true)

    const newUser = {
      ...values,
      phone: cleanIntlPhoneForSubmission(values.phone),
    }

    if (values?.password?.length < 1) {
      delete newUser.password
    }

    try {
      await updateUser(newUser)

      dispatch(
        setAlert({
          isOpen: true,
          title: 'Success',
          message: 'Your account details have been saved.',
        })
      )
    } catch (err) {
      console.error(printAxiosErrors(err, true))

      dispatch(
        setAlert({
          isOpen: true,
          title: err.response?.data?.message,
          type: 'error',
          message: printAxiosErrors(err, true),
        })
      )
    }
  }

  return (
    <DashboardLayout title="Account">
      <Card
        style={{
          width: '100%',
          padding: 48,
          boxShadow: 'none',
        }}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validateOnChange={isFormDirty}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form
              noValidate
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <p style={{ fontSize: 24, fontWeight: 'bold' }}>
                👋 Welcome back, {account?.firstName}!
              </p>
              <br />
              <InputField label="First Name" name="firstName" required />
              <InputField label="Last Name" name="lastName" required />
              <InputField label="Email" name="email" required />
              {defaultCountryISO && (
                <InternationalPhoneInput
                  name="phone"
                  label="Phone"
                  defaultCountry={defaultCountryISO}
                  setFieldValue={setFieldValue}
                  handleChange={(phone) => {
                    const val = phone
                    setFieldValue('phone', val)
                  }}
                />
              )}
              <InputField label="Role" name="role" disabled />
              <MultiselectField
                fullWidth
                label="Location(s)"
                name="locations"
                options={locationOptions}
                helpText=""
                defaultValue={null}
                isDisabled
              />
              <div>
                <a
                  onClick={() => setIsPasswordDialogOpen(true)}
                  style={{ color: Colors['theme-blue'], fontWeight: 'bold' }}
                >
                  🔒 Change Password
                </a>
              </div>
              <button
                className={classNames(buttonStyles.saveBtn)}
                onClick={(e) => isSubmitting && e.preventDefault()}
                type="submit"
                disabled={isSubmitting}
              >
                Sav{isSubmitting ? 'ing...' : 'e'}
              </button>
              <Dialog
                open={isPasswordDialogOpen}
                onClose={() => setIsPasswordDialogOpen(false)}
              >
                <h3
                  style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 20,
                  }}
                >
                  Change Password
                </h3>
                <PasswordChangeForm
                  handleSuccess={() => setIsPasswordDialogOpen(false)}
                  submitButtonText={'Change Password'}
                  userEmail={account.email}
                  userId={account.id}
                />
              </Dialog>

              <EmailVerifyModal
                open={!account?.emailVerifiedAt}
                email={account?.email}
              />
            </Form>
          )}
        </Formik>
        <div style={{ paddingBottom: '60px', paddingTop: '24px' }}>
          <ActionModalTextConfirm
            objectForAction="Account"
            action={deleteAccountAction}
            isActionBlocked={isUserOwner}
            actionBlockedModalContent={
              <>
                <span>
                  You are the Business Owner of this NextMe business. Before
                  deleting your owner account, please transfer ownership to
                  another team member by heading to the team page or contacting
                  us at
                </span>{' '}
                <a
                  className={typeStyles.textLink}
                  href="mailto:support@nextmeapp.com"
                  target="_blank"
                >
                  <span>support@nextmeapp.com</span>
                </a>
                <span>
                  . <br />
                  <br />
                  If you no longer wish to use NextMe at all, you also have the
                  option of deleting your business as a whole. This process will
                  delete your owner user and all other existing data.
                </span>
              </>
            }
            alertSuccessTitle="Your account is being deleted"
            alertSuccessMessage="You will lose access to NextMe momentarily."
            buttonText="Delete Account"
            buttonStyle={['pill', 'red']}
            confirmationString="DELETE"
            modalTitle="Delete Account"
            iconSrc="/icons/delete.svg"
            modalSubtitle="Are you sure you want to delete your account?"
            modalContent="This will erase all of your user information from NextMe and cannot be undone. You will lose access to NextMe immediately."
            modalControlButtons={{
              cancelText: 'Nevermind',
              confirmText: 'Delete',
            }}
            successRedirectRoute="/logout"
          />
        </div>
      </Card>
    </DashboardLayout>
  )
}
